/**
 * Iter 67dg — Vendor "Full rental terms" structured editor.
 *
 * Replaces the single free-form "Terms & Conditions" textarea with an
 * accordion of 8 named sections. Each section maps to its own
 * `terms_*` field on the user record (added to
 * VendorBusinessProfileUpdate). The legacy `terms_and_conditions`
 * field is still surfaced as a 9th "Other / legacy" panel so vendors
 * that already typed something there don't lose it — they can
 * migrate the content into the structured panels at their pace.
 *
 * Used inside VendorPoliciesEditor's "Full rental terms" block.
 */
import { useEffect, useState } from "react";
import { Label } from "@/components/ui/label";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Check, X, KeyRound, ShieldAlert, Fuel, ShoppingBag, Wrench, Info,
} from "lucide-react";

export const RENTAL_TERMS_SECTIONS = [
  {
    key: "terms_included",
    label: "Included in the rental price",
    icon: Check,
    tone: "text-emerald-700",
    placeholder: "e.g. Unlimited mileage, basic insurance with €1000 excess, 24/7 roadside assistance, VAT, additional driver.",
  },
  {
    key: "terms_not_included",
    label: "Not included in the rental price",
    icon: X,
    tone: "text-rose-700",
    placeholder: "e.g. Fuel, tolls, traffic fines, child seats, GPS, cross-border fee, one-way drop-off fee.",
  },
  {
    key: "terms_pickup_requirements",
    label: "What you need at Pick-up",
    icon: KeyRound,
    tone: "text-amber-700",
    placeholder: "e.g. Driving licence held for 2+ years, valid passport / ID, credit card in the main driver's name, voucher / booking confirmation.",
  },
  {
    key: "terms_deposits_excess",
    label: "Deposits, excess and cover",
    icon: ShieldAlert,
    tone: "text-indigo-700",
    placeholder: "e.g. €800 pre-authorised on the credit card at pickup, returned 7–14 days after drop-off. Excess waiver available for €12/day.",
  },
  {
    key: "terms_fuel_policy",
    label: "Fuel policy",
    icon: Fuel,
    tone: "text-sky-700",
    placeholder: "e.g. Pick up full / return full. Otherwise refuelling fee of €25 + missing fuel charged at pump price.",
  },
  {
    key: "terms_extra_services",
    label: "Extra services (payable at counter)",
    icon: ShoppingBag,
    tone: "text-violet-700",
    placeholder: "e.g. Additional driver €4/day, Young driver fee €8/day (21–24), Cross-border permit €15, Out-of-hours pickup €25.",
  },
  {
    key: "terms_extra_equipment",
    label: "Extra Equipment",
    icon: Wrench,
    tone: "text-teal-700",
    placeholder: "e.g. Child seat €3/day, Booster seat €3/day, GPS €4/day, Snow chains €5/day, Roof rack €6/day.",
  },
  {
    key: "terms_important_info",
    label: "Important information",
    icon: Info,
    tone: "text-rose-700",
    placeholder: "e.g. Min driver age 21, smoking forbidden, pets allowed with deposit, mileage cap 200km/day for one-way rentals.",
  },
];

export default function VendorFullRentalTermsEditor({ form, setForm }) {
  // Open the first section by default so the editor is immediately
  // discoverable. Vendors can still collapse everything once they're done.
  const [openValue, setOpenValue] = useState(RENTAL_TERMS_SECTIONS[0].key);
  // Default-open the first not-yet-filled section as the vendor edits.
  useEffect(() => {
    const firstEmpty = RENTAL_TERMS_SECTIONS.find((s) => !(form[s.key] || "").trim());
    if (firstEmpty) setOpenValue(firstEmpty.key);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Accordion
      type="single"
      collapsible
      value={openValue}
      onValueChange={setOpenValue}
      className="rounded-2xl border border-border bg-white divide-y divide-border"
      data-testid="vendor-full-rental-terms"
    >
      {RENTAL_TERMS_SECTIONS.map(({ key, label, icon: Icon, tone, placeholder }) => {
        const value = form[key] || "";
        const filled = value.trim().length > 0;
        return (
          <AccordionItem key={key} value={key} className="border-0">
            <AccordionTrigger
              className="px-4 hover:no-underline"
              data-testid={`vendor-terms-trigger-${key}`}
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <span className={`h-8 w-8 rounded-xl bg-neutral-50 flex items-center justify-center shrink-0 ${tone}`}>
                  <Icon className="h-4 w-4" />
                </span>
                <span className="text-sm font-semibold text-neutral-900 truncate">{label}</span>
                {filled ? (
                  <span className="ml-auto mr-3 text-[10px] uppercase tracking-wider bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100 rounded-full px-2 py-0.5">
                    FILLED
                  </span>
                ) : (
                  <span className="ml-auto mr-3 text-[10px] uppercase tracking-wider bg-amber-50 text-amber-700 ring-1 ring-amber-100 rounded-full px-2 py-0.5">
                    EMPTY
                  </span>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <Label htmlFor={`vendor-terms-${key}`} className="text-[11px] text-neutral-500 mb-1.5 block">
                One short paragraph or a few bullet lines. Whitespace and line breaks are preserved on the public page.
              </Label>
              <textarea
                id={`vendor-terms-${key}`}
                value={value}
                onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                placeholder={placeholder}
                rows={4}
                className="w-full rounded-xl border border-input bg-white px-3 py-2 text-sm placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                data-testid={`vendor-terms-${key}`}
              />
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}
